import { useEffect, useState } from 'react'
import {
    ErrorConstant,
    ReportApprovalService,
} from 'utility'
import {
    APPROVAL_STATUS,
    normalizeApprovalDetail,
    normalizeApprovalListResponse,
} from 'page-components/report-approval/report-approval-utils'

const defaultListFilter = {
    PageNumber: 1,
    PageSize: 20,
    status: APPROVAL_STATUS.Pending,
    CustomerId: '',
    TemplateId: '',
    UserId: '',
    StartDate: '',
    EndDate: '',
    Search: '',
}

// initialStatus: approvers who are not admins may only list their own queue (APPROVAL_STATUS.AwaitingMe)
export const GetPendingApprovalReports = (initialStatus = APPROVAL_STATUS.Pending) => {
    const [data, setData] = useState([])
    const [counters, setCounters] = useState({
        pending: 0,
        approvedThisMonth: 0,
    })
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [filter, setFilter] = useState({ ...defaultListFilter, status: initialStatus })

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')

                const hasExtraFilters = !!(
                    filter.CustomerId ||
                    filter.TemplateId ||
                    filter.UserId ||
                    filter.StartDate ||
                    filter.EndDate ||
                    filter.Search
                )

                const query = {
                    page: filter.PageNumber,
                    // When filters are applied, pull a larger page so client-side
                    // matching works even if the API ignores filter params.
                    size: hasExtraFilters
                        ? Math.max(filter.PageSize || 20, 100)
                        : filter.PageSize,
                    status: filter.status || APPROVAL_STATUS.Pending,
                }

                if (filter.CustomerId) {
                    query.customerId = filter.CustomerId
                }
                if (filter.TemplateId) {
                    query.templateId = filter.TemplateId
                }
                // API list rows use AuditorUserId — send that param name
                if (filter.UserId) {
                    query.auditorUserId = filter.UserId
                    query.userId = filter.UserId
                }
                if (filter.StartDate) query.startDate = filter.StartDate
                if (filter.EndDate) query.endDate = filter.EndDate
                if (filter.Search) query.search = filter.Search

                const result =
                    await ReportApprovalService.listPendingReports(query)
                const normalized = normalizeApprovalListResponse(result)
                setData(normalized.items)
                setTotalCount(normalized.totalCount)
                setCounters(normalized.counters)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [filter])

    const pageChanged = page => {
        setFilter(prev => ({ ...prev, PageNumber: page }))
    }

    const filterChanged = changes => {
        setFilter(prev => ({ ...prev, PageNumber: 1, ...changes }))
    }

    const refresh = () => {
        setFilter(prev => ({ ...prev }))
    }

    return {
        data,
        counters,
        error,
        loading,
        filter,
        totalCount,
        pageChanged,
        filterChanged,
        refresh,
    }
}

export const GetApprovalReportDetail = completedReportId => {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!completedReportId) {
            setData(null)
            return undefined
        }

        let cancelled = false

        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')
                const result = await ReportApprovalService.getReportDetail(
                    completedReportId,
                )
                if (cancelled) return
                setData(normalizeApprovalDetail(result))
            } catch {
                if (!cancelled) setError(ErrorConstant.default)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchData()

        return () => {
            cancelled = true
        }
    }, [completedReportId])

    const refresh = async () => {
        if (!completedReportId) return null
        const result = await ReportApprovalService.getReportDetail(
            completedReportId,
        )
        const normalized = normalizeApprovalDetail(result)
        setData(normalized)
        return normalized
    }

    return { data, error, loading, refresh, setData }
}
